import { useEffect, useState } from "react";
import { Plus, Trash2, ImageIcon } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { api, fmtErr, assetSrc } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import ConfirmDelete from "../ConfirmDelete";

const inputCls =
  "w-full h-12 px-4 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow";

const fmtDate = (d) => {
  try {
    return format(new Date(d), "d MMM yyyy", { locale: es });
  } catch {
    return "";
  }
};

export default function PhotosTab({ machine, isAdmin }) {
  const [photos, setPhotos] = useState(null);
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = () => api.get(`/machines/${machine.id}/photos`).then(({ data }) => setPhotos(data));
  useEffect(() => { load(); }, [machine.id]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (files.length === 0) { toast.error("Selecciona al menos una foto"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("description", description);
      files.forEach((f) => fd.append("files", f));
      await api.post(`/machines/${machine.id}/photos`, fd);
      toast.success("Fotos añadidas");
      setOpen(false);
      setFiles([]);
      setDescription("");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (photo) => {
    try {
      await api.delete(`/photos/${photo.id}`);
      toast.success("Foto eliminada");
      setLightbox(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="photos-tab">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-xl font-semibold tracking-tight">Fotos</h2>
        {isAdmin && (
          <button
            onClick={() => setOpen(true)}
            data-testid="add-photos-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 active:scale-95 transition-[background-color,transform] duration-200"
          >
            <Plus size={15} /> Añadir fotos
          </button>
        )}
      </div>

      {photos === null ? (
        <div className="flex justify-center py-12">
          <div className="h-7 w-7 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin" />
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-2xl border border-dashed border-zinc-200" data-testid="photos-empty">
          <ImageIcon className="mx-auto text-zinc-300 mb-3" size={32} strokeWidth={1.5} />
          <p className="text-zinc-500 text-sm">Todavía no hay fotografías.</p>
          <p className="text-zinc-400 text-sm mt-1">Máquina completa, placa de características, averías…</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {photos.map((p) => (
            <button
              key={p.id}
              onClick={() => setLightbox(p)}
              data-testid={`photo-item-${p.id}`}
              className="relative aspect-square rounded-2xl overflow-hidden bg-zinc-100 group focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-2"
            >
              <img
                src={assetSrc(p)}
                alt={p.description || "Foto de la máquina"}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                loading="lazy"
              />
              {(p.description || p.created_at) && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent p-3 pt-12 text-left">
                  {p.description && <p className="text-white text-xs font-medium truncate">{p.description}</p>}
                  <p className="text-white/70 text-[11px] capitalize">{fmtDate(p.created_at)}</p>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="photos-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Añadir fotos</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 pt-2">
            <label className="flex flex-col items-center justify-center gap-2 h-28 rounded-2xl border border-dashed border-zinc-300 cursor-pointer hover:border-zinc-400 transition-colors text-sm text-zinc-500" data-testid="photos-file-drop">
              <ImageIcon size={20} className="text-zinc-400" />
              {files.length > 0 ? `${files.length} foto(s) seleccionada(s)` : "Toca para seleccionar fotos"}
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                data-testid="photos-file-input"
              />
            </label>
            <input
              className={inputCls}
              placeholder="Descripción (opcional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="photos-description-input"
            />
            <button
              type="submit"
              disabled={saving}
              data-testid="photos-upload-submit"
              className="w-full h-12 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-50"
            >
              {saving ? "Subiendo…" : "Subir fotos"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(lightbox)} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="rounded-3xl sm:max-w-2xl p-3" data-testid="photo-lightbox" aria-describedby={undefined}>
          {lightbox && (
            <div>
              <img
                src={assetSrc(lightbox)}
                alt={lightbox.description || "Foto ampliada"}
                className="w-full rounded-2xl max-h-[70vh] object-contain bg-zinc-100"
              />
              <div className="flex items-center justify-between px-3 py-3">
                <div>
                  {lightbox.description && <p className="text-sm font-medium">{lightbox.description}</p>}
                  <p className="text-xs text-zinc-400 capitalize">{fmtDate(lightbox.created_at)}</p>
                </div>
                {isAdmin && (
                  <ConfirmDelete
                    testid="photo-delete-button"
                    title="¿Eliminar esta foto?"
                    description="La fotografía se eliminará de forma permanente."
                    onConfirm={() => handleDelete(lightbox)}
                  />
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
